/** @file facet.setDrawnAngles: replaces the drawn azimuths and/or drawn screen angles of one entry. */
import { tileCountOf } from '@zilo/pose3d-format/tables/tileCountOf';
import { checkArray } from '@zilo/pose3d-format/validate/fields/checkArray';
import { checkNumber } from '@zilo/pose3d-format/validate/fields/checkNumber';
import { failField } from '@zilo/pose3d-format/validate/fields/failField';
import type { CommandDef } from '../apply/commandDef';
import { checkPuppet } from '../puppetDoc/checkPuppet';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import { checkAngleCount } from './checkAngleCount';
import { entryInverse } from './entryInverse';
import { findEntry } from './findEntry';
import { requireSectorTable } from './requireSectorTable';

const angles = checkArray(checkNumber());
const ANGLE_KEYS = ['drawnAzimuths', 'drawnScreenAngles'] as const;

/**
 * `facet.setDrawnAngles { drawable, entry, drawnAzimuths?, drawnScreenAngles? }` → `{}` (at least one array, one
 * value per drawn tile). Inverse: the previous arrays, or E when a given array was absent before.
 */
export const facetSetDrawnAnglesCommand: CommandDef<'facet.setDrawnAngles'> = {
  name: 'facet.setDrawnAngles',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['drawable', 'entry', ...ANGLE_KEYS]);
    if (!r.has('drawnAzimuths') && !r.has('drawnScreenAngles')) failField(path, 'drawnAzimuths and/or drawnScreenAngles', undefined);
    const read = (k: string): number[] => r.check(k, angles) as number[];
    return compact({ drawable: r.str('drawable'), entry: r.int('entry', { min: 0 }), drawnAzimuths: r.opt('drawnAzimuths', read), drawnScreenAngles: r.opt('drawnScreenAngles', read) });
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const { entry } = findEntry(draft.puppet, input.drawable, input.entry);
    const tiles = tileCountOf(requireSectorTable(draft.puppet, entry.yaw, ctx.sectorTables, '/input/entry'));
    const given = ANGLE_KEYS.filter((k) => input[k] !== undefined);
    for (const k of given) checkAngleCount(input[k], tiles, `/input/${k}`);
    const inverse = given.every((k) => entry[k] !== undefined)
      ? [{ name: 'facet.setDrawnAngles' as const, input: { drawable: input.drawable, entry: input.entry, ...Object.fromEntries(given.map((k) => [k, [...(entry[k] ?? [])]])) } }]
      : entryInverse(input.drawable, input.entry, entry);
    for (const k of given) entry[k] = [...(input[k] ?? [])];
    checkPuppet(draft, ctx);
    return { result: {}, warnings: [], inverse };
  },
};
